import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, ArrowRightLeft, UserPlus } from "lucide-react";
import { getWeekGlance } from "../../lib/api";
import { workoutTypeLabel, workoutTypeColor, formatSegmentValue } from "../../lib/coaching";

// Soft per-athlete row tints — warm/cool pastels that sit on ivory without
// fighting the navy/burgundy type-badge accents.
const ROW_TINTS = ["#F3EDE4", "#E8EEF4", "#F4E8EA", "#EAF1E6", "#F5EFE0", "#ECE8F2", "#E6F0F1", "#F4ECE6"];
const DAY_HEAD = ["M", "T", "W", "T", "F", "S", "S"];

const localDay = (s) => new Date(`${s}T00:00:00`);
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

function detailLine(wo) {
  if (wo.duration_or_distance) return wo.duration_or_distance;
  const segs = wo.segments || [];
  if (segs.length) {
    const main = segs.find((s) => s.kind === "group") || segs.find((s) => !/warm|cool/i.test(s.label || "")) || segs[0];
    if (main.kind === "group") {
      const inner = (main.items || []).map(formatSegmentValue).filter(Boolean).join(" / ");
      return `${main.repeat_count || ""}× ${inner}`.trim();
    }
    return formatSegmentValue(main);
  }
  const blocks = wo.strength_blocks || [];
  if (blocks.length) {
    const n = blocks.reduce((acc, b) => acc + (b.exercises || []).length, 0);
    return n ? `${n} exercise${n === 1 ? "" : "s"}` : "";
  }
  if (wo.add_strides) return `+ ${wo.strides_count || ""} strides`.replace("  ", " ");
  return wo.structure || "";
}

function GlanceWorkout({ wo, past, idx }) {
  const done = wo.status === "complete" || wo.status === "partial";
  const faded = past && !done;
  const color = workoutTypeColor(wo.type);
  const title = wo.title || workoutTypeLabel(wo.type);
  const detail = detailLine(wo);
  return (
    <div
      className={`relative pl-2 py-1 border-l-2 ${faded ? "opacity-40" : ""} ${idx > 0 ? "mt-1.5" : ""}`}
      style={{ borderColor: color }}
      data-testid={`glance-workout-${wo.id}`}
      title={`${title}${detail ? ` · ${detail}` : ""}`}
    >
      <div className="flex items-center gap-1 min-w-0">
        <span className="text-[8px] tracking-[0.16em] uppercase font-semibold px-1 py-px rounded-sm text-white truncate max-w-[70%]" style={{ background: color }} data-testid="glance-type-badge">
          {workoutTypeLabel(wo.type)}
        </span>
        {done && <Check size={11} strokeWidth={3} className="text-emerald-700 shrink-0" data-testid="glance-done-tick" />}
        {wo.shifted_from_day && <ArrowRightLeft size={10} className="text-[var(--tir-burgundy)] shrink-0" title={`Moved from ${wo.shifted_from_day}`} />}
        {wo.added_by_athlete && <UserPlus size={10} className="text-[var(--tir-burgundy)] shrink-0" title="Added by athlete" />}
      </div>
      <div className={`text-[11px] leading-tight font-medium text-[var(--tir-ink)] truncate ${done ? "line-through decoration-[var(--tir-muted)]/60" : ""}`}>
        {wo.is_optional ? "(opt) " : ""}{wo.is_alternative ? "or · " : ""}{title}
      </div>
      {detail && <div className="text-[10px] leading-tight text-[var(--tir-muted)] truncate">{detail}</div>}
    </div>
  );
}

export default function WeekGlanceTable() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    // Anchor on the coach's LOCAL date (server clock is UTC) and refresh
    // whenever the tab regains focus so program edits show up immediately.
    const load = () => getWeekGlance({ week_start: iso(new Date()) }).then(setData).catch(() => setErr("Couldn't load this week's schedule"));
    load();
    const onVisible = () => { if (document.visibilityState === "visible") load(); };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, []);

  if (err) return <p className="text-sm text-[var(--tir-burgundy)] mb-10" data-testid="week-glance-error">{err}</p>;
  if (!data) return <div className="mb-10 h-40 bg-[var(--tir-ivory)] border border-[var(--tir-border)] animate-pulse" data-testid="week-glance-loading" />;
  if (!data.athletes.length) return null;

  const rangeLabel = `${localDay(data.dates[0]).toLocaleDateString(undefined, { month: "short", day: "numeric" })} – ${localDay(data.dates[6]).toLocaleDateString(undefined, { month: "short", day: "numeric" })}`;

  return (
    <section className="mb-10" data-testid="week-glance">
      <div className="flex items-baseline justify-between mb-3">
        <h2 className="text-[10px] tracking-[0.28em] uppercase font-semibold text-[var(--tir-muted)] font-mono-tir">
          Week at a glance · <span data-testid="week-glance-range">{rangeLabel}</span>
        </h2>
        <span className="hidden sm:inline text-[9px] tracking-[0.18em] uppercase font-mono-tir text-[var(--tir-muted)]">Tap a day to edit that week</span>
      </div>
      <div className="overflow-x-auto border border-[var(--tir-border)] bg-white">
        <table className="w-full min-w-[880px] border-separate border-spacing-0 table-fixed" data-testid="week-glance-table">
          <thead>
            <tr className="bg-[var(--tir-navy)] text-white">
              <th className="sticky left-0 z-20 bg-[var(--tir-navy)] w-28 sm:w-36 text-left px-3 py-2 text-[10px] tracking-[0.22em] uppercase font-semibold font-mono-tir shadow-[4px_0_8px_-4px_rgba(0,0,0,0.35)]" data-testid="week-glance-athlete-col">Athlete</th>
              {data.dates.map((d, i) => {
                const today = d === data.today;
                return (
                  <th key={d} className={`px-2 py-2 text-center ${today ? "bg-[var(--tir-burgundy)]" : ""}`} data-testid={today ? "week-glance-today-col" : undefined}>
                    <div className="text-[11px] font-semibold tracking-[0.2em]">{DAY_HEAD[i]}</div>
                    <div className="text-[9px] opacity-80 font-mono-tir">{localDay(d).getDate()}</div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {data.athletes.map((a, ri) => {
              const tint = ROW_TINTS[ri % ROW_TINTS.length];
              const hasPlan = !!a.program_id;
              const goEdit = () => hasPlan && navigate(`/coaching/programs/${a.program_id}/edit?week=${a.week_number || 1}`);
              return (
                <tr key={a.athlete_id} style={{ background: tint }} className="align-top [&>td]:border-t [&>td]:border-[var(--tir-border)]" data-testid={`week-glance-row-${a.athlete_id}`}>
                  <td className="sticky left-0 z-10 px-3 py-2 shadow-[4px_0_8px_-4px_rgba(0,0,0,0.18)]" style={{ background: tint }} data-testid={`week-glance-sticky-${a.athlete_id}`}>
                    <button type="button" onClick={() => navigate(`/coaching/athletes/${a.athlete_id}`)} className="text-left font-semibold text-[13px] text-[var(--tir-ink)] hover:text-[var(--tir-burgundy)] leading-tight break-words" data-testid={`week-glance-name-${a.athlete_id}`}>
                      {a.name}
                    </button>
                    {hasPlan && <div className="text-[9px] tracking-[0.14em] uppercase text-[var(--tir-muted)] font-mono-tir mt-0.5">Week {a.week_number}</div>}
                  </td>
                  {!hasPlan ? (
                    <td colSpan={7} className="px-3 py-3 text-[11px] text-[var(--tir-muted)] italic" data-testid={`week-glance-noplan-${a.athlete_id}`}>No program scheduled this week</td>
                  ) : data.dates.map((d) => {
                    const items = a.days[d] || [];
                    const past = d < data.today;
                    return (
                      <td
                        key={d}
                        onClick={goEdit}
                        className={`px-1.5 py-1.5 cursor-pointer hover:bg-white/60 transition-colors ${d === data.today ? "ring-1 ring-inset ring-[var(--tir-burgundy)]/40" : ""}`}
                        data-testid={`week-glance-cell-${a.athlete_id}-${d}`}
                      >
                        {items.length === 0
                          ? <div className="text-center text-[var(--tir-muted)]/40 text-xs py-2">—</div>
                          : items.map((wo, i) => <GlanceWorkout key={wo.id} wo={wo} past={past} idx={i} />)}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}
